import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Link } from 'react-router-dom';
import { useL10n } from '@uplift/hooks';
import { Button, StackableCard } from '@uplift/ui-v2';

import EventBusyIcon from '@mui/icons-material/EventBusy';

import { ScheduleModals } from '../../../../components';
import { useCurrentuser } from '@uplift/contexts';
import { ROUTING_CONFIG, findPsychiatryRoute } from '@uplift/constants';
import { PsychAppointmentBookingSource } from '@uplift/api';


const NoUpcomingSessionsMatchMessage: React.VFC<{ needToMatchTherapist?: boolean; needToMatchPsychiatrist?: boolean }> = ({ needToMatchTherapist, needToMatchPsychiatrist }) => {
  const l10n = useL10n();
  const { classes } = useStyles();

  return (
    <>
      <Box mb={3}>
        <Typography variant="body1" color="textSecondary">
          {l10n.noSessionsScheduledForToday.matchWithProvider}
        </Typography>
      </Box>
      <Box className={classes.buttonRow} mb={1}>
        {needToMatchPsychiatrist && (
          <Button
            component={Link}
            to={findPsychiatryRoute.createLink(null, { source: PsychAppointmentBookingSource.clientHomeDashboard })}
            variant="contained"
            color="secondary"
          >
            {l10n.noSessionsScheduledForToday.findAPsychiatrist}
          </Button>
        )}
        {needToMatchTherapist && (
          <Button component={Link} to={ROUTING_CONFIG.matching} variant="outlined" color="secondary">
            {l10n.noSessionsScheduledForToday.findATherapist}
          </Button>
        )}
      </Box>
    </>
  );
};


const NoUpcomingSessionsMessage: React.VFC = () => {
  const l10n = useL10n();

  return (
    <>
      <Box mb={3}>
        <Typography variant="body1" color="textSecondary">
          {l10n.noSessionsScheduledForToday.emailYourProvider}
        </Typography>
      </Box>
      <Button component={Link} to={ROUTING_CONFIG.careTeam} variant="contained" color="secondary">
        {l10n.noSessionsScheduledForToday.bookSession}
      </Button>
    </>
  );
};


const useStyles = makeStyles()(theme => ({
  wrapper: {
    whiteSpace: 'initial',
    textAlign: 'center',
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(6),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      whiteSpace: 'pre-wrap',
    }
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'center',
    }
  }
}));

export type NoUpcomingSessionsProps = {
  needToMatchTherapist?: boolean;
  needToMatchPsychiatrist?: boolean;
};

export const NoUpcomingSessions: React.VFC<NoUpcomingSessionsProps> = ({ needToMatchTherapist, needToMatchPsychiatrist }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const user = useCurrentuser();

  return (
    <div>
      <StackableCard className={classes.wrapper}>
        <Box color="#D5D2C7" fontSize="60px">
          <EventBusyIcon fontSize="inherit" />
        </Box>
        <Box mb={1}>
          <Typography variant="h6" color="textSecondary">
            {l10n.noSessionsScheduledForToday.noUpcomingSessions}
          </Typography>
        </Box>
        {needToMatchTherapist || needToMatchPsychiatrist
          ? <NoUpcomingSessionsMatchMessage needToMatchTherapist={needToMatchTherapist} needToMatchPsychiatrist={needToMatchPsychiatrist} />
          : <NoUpcomingSessionsMessage />
        }
        <ScheduleModals patientId={user.id} />
      </StackableCard>
    </div>
  );
};
